import { useState, useCallback, useMemo } from "react";
import {
  ContextualSaveBar,
  Page,
  Layout,
  BlockStack,
  Banner,
  Text,
  Loading,
} from "@medentic/polaris-react";
import { DeleteConfirmationModal } from "@shared/ui";
import { DeleteIcon, ExternalIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { AppPageWrapper } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Brand, GeoAddress } from "@features/events/types";
import usePageMeta from "@shared/hooks/common/usePageMeta";
import { validateBrandRequiredFields } from "@shared/lib";
import { getLocalizedText } from "@shared/lib";
import { showToast } from "@shared/lib";
import { RootState } from "@app/store/store";
import StatusCard from "@features/events/components/StatusCard";
import { StatusEntityType } from "@features/events/types";
import TranslationsCard, { TranslationsEntityType } from "@features/events/components/TranslationsCard";

import {
  OverviewCard,
  ContactInformationCard,
  SocialMediaProfilesCard,
  BrandingMediaCard,
} from "../brand-profile/components";
import { BrandDetailsSkeleton } from "./components";

// Helper to sanitize GeoAddress by removing any bounds property from API response
const sanitizeGeoAddress = (geoAddress: GeoAddress | undefined): GeoAddress | undefined => {
  if (!geoAddress) return undefined;
  const { bounds: _bounds, ...rest } = geoAddress as GeoAddress & {
    bounds?: unknown;
  };
  void _bounds;
  return rest as GeoAddress;
};

const NEW_BRAND_INITIAL_DATA: Partial<Brand> = {
  name: {},
  headline: {},
  description: {},
  slug: undefined,
  geoAddress: undefined,
  contactEmail: undefined,
  contactPhone: undefined,
  contactPhoneCountryCode: "+359",
  website: undefined,
  visibility: 1,
  verified: false,
  featured: false,
  languageCodes: [],
  social: [],
};

const mapBrandToDraftData = (brand: Brand | undefined): Partial<Brand> => {
  if (!brand) {
    return {};
  }

  return {
    id: brand.id,
    slug: brand.slug,
    name: brand.name,
    headline: brand.headline,
    description: brand.description,
    visibility: brand.visibility,
    verified: brand.verified,
    featured: brand.featured,
    logoRectangle: brand.logoRectangle,
    logoSquare: brand.logoSquare,
    website: brand.website,
    social: brand.social,
    contactEmail: brand.contactEmail,
    contactPhone: brand.contactPhone,
    contactPhoneCountryCode: brand.contactPhoneCountryCode,
    languageCodes: brand.languageCodes,
    geoAddress: sanitizeGeoAddress(brand.geoAddress),
    customButtonLabel: brand.customButtonLabel,
    customButtonLink: brand.customButtonLink,
  };
};

export default function BrandDetails() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, brandId } = useParams<{
    eventOrganizerId: string;
    brandId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { GET, POST, PUT, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );

  // Determine if we're creating a new brand
  const isNewBrand = brandId === "new";

  // State
  const [brandDataDraft, setBrandDataDraft] = useState<Partial<Brand> | null>(
    isNewBrand ? NEW_BRAND_INITIAL_DATA : null
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(isNewBrand); // New brands start as dirty
  const [translationsExpanded, setTranslationsExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch brand data (only for existing brands)
  const {
    data: brand,
    isLoading,
    isFetching,
  } = useQuery<Brand>({
    queryKey: ["brand", hcc, brandId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${hcc}/e/brands/${brandId}`),
    enabled: Boolean(hcc && brandId && !isNewBrand),
  });

  // Prepare page meta data
  const brandName = getLocalizedText(brand?.name);
  const brandLogoUrl = brand?.logoSquare?.[0]?.src || brand?.logoRectangle?.[0]?.src;

  // Update page meta when brand data is available
  usePageMeta(brandName, brandLogoUrl);

  const initialBrandData = useMemo(
    () => (isNewBrand ? NEW_BRAND_INITIAL_DATA : mapBrandToDraftData(brand)),
    [isNewBrand, brand]
  );
  const brandData = brandDataDraft ?? initialBrandData;

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Brand>) => {
      if (isNewBrand) {
        return POST(`/api/healthcare-companies/${hcc}/e/brands`, data);
      } else {
        return PUT(
          `/api/healthcare-companies/${hcc}/e/brands/${brandId}`,
          data
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (data: any) => {
      if (isNewBrand && data?.id) {
        // For new brands, navigate to the created brand's detail page
        queryClient.invalidateQueries({ queryKey: ["brands", hcc] });
        dispatch(
          showToast({
            message: t("brandDetails.saveSuccess"),
            type: "success",
          })
        );
        navigate(`/e/${hcc}/brands/${data.id}`);
      } else {
        queryClient.invalidateQueries({
          queryKey: ["brand", hcc, brandId],
        });
        queryClient.invalidateQueries({ queryKey: ["brands", hcc] });
        setBrandDataDraft(null);
        dispatch(
          showToast({
            message: t("brandDetails.saveSuccess"),
            type: "success",
          })
        );
      }
      setIsDirty(false);
      setFieldErrors({});
    },
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string>, message?: string } };
        };
        if (apiError.response?.data?.errors) {
          setFieldErrors(apiError.response.data.errors);
        }
        // Show error toast
        const errorMessage = apiError.response?.data?.message || t("brandDetails.saveError", { defaultValue: "Failed to save brand" });
        dispatch(
          showToast({
            message: errorMessage,
            type: "error",
          })
        );
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${hcc}/e/brands/${brandId}`),
    onSuccess: () => {
      // Invalidate queries and navigate back to brands list
      queryClient.invalidateQueries({ queryKey: ["brands", hcc] });
      dispatch(
        showToast({
          message: t("brandDetails.deleteSuccess"),
          type: "success",
        })
      );
      navigate(`/e/${hcc}/brands${location.search}`);
    },
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { message?: string } };
        };
        const errorMessage = apiError.response?.data?.message || t("brandDetails.deleteError", { defaultValue: "Failed to delete brand" });
        dispatch(
          showToast({
            message: errorMessage,
            type: "error",
          })
        );
      }
    },
  });

  // Helper function to validate multilingual fields in real-time
  const validateMultilingualField = useCallback(
    (
      field: Record<string, string> | undefined,
      translationKey: string
    ): string | undefined => {
      if (!field) return undefined;

      // Check if any language has content
      const hasAnyContent = Object.values(field).some((value) => value?.trim());

      if (hasAnyContent && selectedLanguages.length > 0) {
        // If any language has content, all selected languages must have content
        const missingLanguages = selectedLanguages.filter(
          (lang) => !field[lang]?.trim()
        );
        if (missingLanguages.length > 0) {
          return t("common.requiredWithField", {
            field_name: t(translationKey),
            missing_languages: missingLanguages.join(", "),
            defaultValue: `${t(
              translationKey
            )} is missing content for: ${missingLanguages.join(", ")}`,
          });
        }
      }
      return undefined;
    },
    [selectedLanguages, t]
  );

  // Handle data changes
  const handleDataChange = useCallback(
    (updates: Partial<Brand>) => {
      const newBrandData = { ...brandData, ...updates };
      setBrandDataDraft(newBrandData);
      setIsDirty(true);

      // Clear validation errors for fields that are now valid
      setFieldErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        Object.keys(updates).forEach((field) => {
          switch (field) {
            case "name": {
              if (updates.name) {
                if (selectedLanguages.length > 0) {
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.name?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors.name;
                  }
                } else {
                  if (
                    Object.values(updates.name).some((value) =>
                      value?.trim()
                    )
                  ) {
                    delete newErrors.name;
                  }
                }
              }

              const nameError = validateMultilingualField(
                newBrandData.name,
                "brandProfile.overview.name"
              );
              if (nameError) {
                newErrors.name = nameError;
              } else if (
                !nameError &&
                newErrors.name?.includes("missing content")
              ) {
                delete newErrors.name;
              }
              break;
            }
            case "headline": {
              if (updates.headline) {
                if (selectedLanguages.length > 0) {
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.headline?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors.headline;
                  }
                } else {
                  if (
                    Object.values(updates.headline).some((value) =>
                      value?.trim()
                    )
                  ) {
                    delete newErrors.headline;
                  }
                }
              }

              const headlineError = validateMultilingualField(
                newBrandData.headline,
                "brandProfile.overview.headline"
              );
              if (headlineError) {
                newErrors.headline = headlineError;
              } else if (
                !headlineError &&
                newErrors.headline?.includes("missing content")
              ) {
                delete newErrors.headline;
              }
              break;
            }
            case "description": {
              if (updates.description) {
                if (selectedLanguages.length > 0) {
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.description?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors.description;
                  }
                } else {
                  if (
                    Object.values(updates.description).some((value) =>
                      value?.trim()
                    )
                  ) {
                    delete newErrors.description;
                  }
                }
              }

              const descriptionError = validateMultilingualField(
                newBrandData.description,
                "brandProfile.overview.description"
              );
              if (descriptionError) {
                newErrors.description = descriptionError;
              } else if (
                !descriptionError &&
                newErrors.description?.includes("missing content")
              ) {
                delete newErrors.description;
              }
              break;
            }
            case "slug":
              if (updates.slug?.trim()) {
                delete newErrors.slug;
              }
              break;
            case "geoAddress":
              if (updates.geoAddress && updates.geoAddress.googlePlaceId) {
                delete newErrors.geoAddress;
              }
              break;
            case "languageCodes":
              if (updates.languageCodes && updates.languageCodes.length > 0) {
                delete newErrors.languageCodes;
              }
              break;
            case "visibility":
              if (updates.visibility !== undefined && updates.visibility !== null) {
                delete newErrors.visibility;
              }
              break;
            default:
              break;
          }
        });

        return newErrors;
      });
    },
    [brandData, selectedLanguages, validateMultilingualField]
  );

  // Handle save
  const handleSave = useCallback(() => {
    // Validate required fields before saving
    const validationResult = validateBrandRequiredFields(
      brandData,
      t,
      selectedLanguages
    );

    if (!validationResult.isValid) {
      // Set field errors
      setFieldErrors(validationResult.errors);

      // Show toast notification
      const errorCount = Object.keys(validationResult.errors).length;
      const translationKey =
        errorCount === 1
          ? "common.validation.requiredFieldsError_one"
          : "common.validation.requiredFieldsError_other";

      dispatch(
        showToast({
          message: t(translationKey, {
            count: errorCount,
            defaultValue: `Please fill in all required fields. ${errorCount} field${
              errorCount > 1 ? "s" : ""
            } need${errorCount > 1 ? "" : "s"} attention.`,
          }),
          type: "error",
        })
      );
      return;
    }

    // Filter out undefined values and clean the data before sending
    const cleanData = Object.entries(brandData).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          // Special handling for empty strings in optional fields
          if (key === "customButtonLink" && value === "") {
            // Don't include empty customButtonLink
            return acc;
          }
          // Don't send id, link, createdAt, updatedAt, isMy, isUsedBefore, role, actions
          if (!["id", "link", "createdAt", "updatedAt", "isMy", "isUsedBefore", "role", "actions"].includes(key)) {
            (acc as Record<string, unknown>)[key] = value;
          }
        }
        return acc;
      },
      {} as Partial<Brand>
    );

    saveMutation.mutate(cleanData);
  }, [brandData, saveMutation, t, selectedLanguages, dispatch]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    if (isNewBrand) {
      // For new brands, navigate back to list
      navigate(`/e/${hcc}/brands${location.search}`);
    } else {
      // For existing brands, reset to original data
      if (brand) {
        setBrandDataDraft(null);
      }
      setIsDirty(false);
      setFieldErrors({});
    }
  }, [isNewBrand, brand, navigate, hcc, location.search]);

  // Handle preview
  const brandLink = brand?.link;
  const handlePreview = useCallback(() => {
    if (brandLink) {
      window.open(brandLink, "_blank");
    }
  }, [brandLink]);

  // Handle delete
  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteMutation.mutate();
    setShowDeleteModal(false);
  }, [deleteMutation]);

  // Loading state
  if (isLoading || (isFetching && !brand)) {
    return (
      <AppPageWrapper>
        <BrandDetailsSkeleton />
      </AppPageWrapper>
    );
  }

  // Determine page title
  const pageTitle = isNewBrand
    ? t("brandDetails.newBrand")
    : brandName || t("brandDetails.title");

  // Determine if delete should be disabled
  const isDeleteDisabled = !brandData.verified;

  return (
    <AppPageWrapper>
      {isDirty && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            content: isNewBrand ? t("common.create") : t("common.save"),
            onAction: handleSave,
            loading: saveMutation.isPending,
          }}
          discardAction={
            !isNewBrand
              ? {
                  onAction: handleDiscard,
                }
              : undefined
          }
        />
      )}

      {isFetching && <Loading />}

      <Page
        backAction={{ content: t("brands.title"), url: `/e/${hcc}/brands${location.search}` }}
        title={pageTitle}
        primaryAction={
          !isNewBrand && brand?.link
            ? {
                content: t("brandDetails.actions.preview"),
                icon: ExternalIcon,
                onAction: handlePreview,
              }
            : undefined
        }
        secondaryActions={
          !isNewBrand
            ? [
                {
                  content: t("common.delete"),
                  icon: DeleteIcon,
                  destructive: true,
                  disabled: isDeleteDisabled,
                  onAction: handleDelete,
                  helpText: isDeleteDisabled
                    ? t("brandDetails.deleteDisabledNotVerified")
                    : undefined,
                },
              ]
            : []
        }
      >
        {isNewBrand && (
          <BlockStack gap="400">
            <Banner tone="info">
              <Text as="p">
                {t("brandDetails.createModeLogoMessage")}
              </Text>
            </Banner>
          </BlockStack>
        )}

        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              <OverviewCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
                hcc={hcc}
              />

              {!isNewBrand && (
                <BrandingMediaCard
                  brandData={JSON.parse(JSON.stringify(brandData))}
                  onDataChange={handleDataChange}
                  fieldErrors={fieldErrors}
                />
              )}

              <ContactInformationCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <SocialMediaProfilesCard
                brandData={JSON.parse(JSON.stringify(brandData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />
            </BlockStack>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <StatusCard 
                entityType={StatusEntityType.BRAND}
                featured={brandData.featured ?? null}
                verified={brandData.verified ?? false}
                visibility={brandData.visibility ?? 0}
                onDataChange={handleDataChange} 
              />

              <TranslationsCard
                translationsExpanded={translationsExpanded}
                setTranslationsExpanded={setTranslationsExpanded}
                entityType={TranslationsEntityType.BRAND}
              />
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>

      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title={t("brandDetails.deleteConfirm")}
        loading={deleteMutation.isPending}
      />
    </AppPageWrapper>
  );
}
